import { useCallback } from "react";
import {
  QUERY_COMBINATORS,
  createRule,
  createRuleGroup,
  isRuleGroup,
} from "../../../logic/services/queryBuilderConfig.js";
import { rbInputStyle, rbSelectStyle } from "../reportBuilderTheme.js";

function cloneQuery(query) {
  return JSON.parse(JSON.stringify(query));
}

function btn(c, { danger = false } = {}) {
  return {
    height: 30,
    padding: "0 10px",
    borderRadius: 7,
    border: `1px solid ${danger ? "#EF444444" : c.cardBdr}`,
    background: c.card,
    color: danger ? "#EF4444" : c.mid,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
}

function buildFields(fieldNames) {
  return fieldNames.map((name) => ({
    name,
    label: name.replace(/_/g, " "),
    inputType: /speed|fuel|odometer|rpm|distance|score/i.test(name) ? "number" : "text",
    values:
      name === "ignition"
        ? [
            { name: "ON", label: "ON" },
            { name: "OFF", label: "OFF" },
          ]
        : name === "status"
          ? [
              { name: "active", label: "active" },
              { name: "inactive", label: "inactive" },
            ]
          : undefined,
  }));
}

function RuleRow({ rule, fields, operators, onUpdate, onRemove, onClone, c, A }) {
  const focus = (e) => (e.currentTarget.style.borderColor = `${A}66`);
  const blur = (e) => (e.currentTarget.style.borderColor = c.cardBdr);
  const fieldMeta = fields.find((f) => f.name === rule.field);
  const hideValue = rule.operator === "null" || rule.operator === "notNull";
  const isExpr = rule.operator === "expr" || rule.operator === "case";

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: c.mid }}>
        <input type="checkbox" checked={!!rule.not} onChange={(e) => onUpdate({ not: e.target.checked })} />
        NOT
      </label>
      <select value={rule.field ?? ""} onChange={(e) => onUpdate({ field: e.target.value })} style={{ ...rbSelectStyle(c), minWidth: 130 }} onFocus={focus} onBlur={blur}>
        <option value="">Field…</option>
        {fields.map((f) => (
          <option key={f.name} value={f.name}>
            {f.label}
          </option>
        ))}
      </select>
      <select value={rule.operator ?? "="} onChange={(e) => onUpdate({ operator: e.target.value })} style={{ ...rbSelectStyle(c), minWidth: 120 }} onFocus={focus} onBlur={blur}>
        {operators.map((op) => (
          <option key={op.name} value={op.name}>
            {op.label}
          </option>
        ))}
      </select>
      {!hideValue ? (
        fieldMeta?.values ? (
          <select value={rule.value ?? ""} onChange={(e) => onUpdate({ value: e.target.value })} style={{ ...rbSelectStyle(c), minWidth: 120 }}>
            <option value="">Value…</option>
            {fieldMeta.values.map((v) => (
              <option key={v.name} value={v.name}>
                {v.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={fieldMeta?.inputType === "number" ? "number" : "text"}
            value={rule.value ?? ""}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder={isExpr ? "expression" : rule.operator === "between" ? "min, max" : rule.operator === "in" ? "a, b, c" : "Value"}
            style={{ ...rbInputStyle(c, false, { mono: isExpr }), minWidth: 140, flex: 1 }}
            onFocus={focus}
            onBlur={blur}
          />
        )
      ) : null}
      <button type="button" onClick={onRemove} style={btn(c, { danger: true })} aria-label="Remove rule">
        ×
      </button>
      <button type="button" onClick={onClone} style={btn(c)} aria-label="Clone rule">
        ⧉
      </button>
    </div>
  );
}

function RuleGroupEditor({ group, path, fields, operators, onQueryChange, c, A, depth = 0 }) {
  const replaceGroup = useCallback(
    (nextGroup) => {
      onQueryChange((root) => {
        const copy = cloneQuery(root);
        if (path.length === 0) return nextGroup;
        let cursor = copy;
        for (let i = 0; i < path.length - 1; i += 1) cursor = cursor.rules[path[i]];
        cursor.rules[path[path.length - 1]] = nextGroup;
        return copy;
      });
    },
    [path, onQueryChange],
  );

  const patchSelf = (patch) => replaceGroup({ ...group, ...patch });

  const mutateRoot = (mutator) => {
    onQueryChange((root) => {
      const copy = cloneQuery(root);
      mutator(copy, path);
      return copy;
    });
  };

  const addItem = (item) => {
    mutateRoot((copy, p) => {
      let cursor = copy;
      for (const idx of p) cursor = cursor.rules[idx];
      cursor.rules.push(item);
    });
  };

  const removeChild = (index) => {
    mutateRoot((copy, p) => {
      let cursor = copy;
      for (const idx of p) cursor = cursor.rules[idx];
      cursor.rules.splice(index, 1);
    });
  };

  const updateChild = (index, patch) => {
    mutateRoot((copy, p) => {
      let cursor = copy;
      for (const idx of p) cursor = cursor.rules[idx];
      cursor.rules[index] = { ...cursor.rules[index], ...patch };
    });
  };

  const removeSelf = () => {
    if (path.length === 0) return;
    onQueryChange((root) => {
      const copy = cloneQuery(root);
      let cursor = copy;
      for (let i = 0; i < path.length - 1; i += 1) cursor = cursor.rules[path[i]];
      cursor.rules.splice(path[path.length - 1], 1);
      return copy;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: depth ? 12 : 0, borderLeft: depth ? `2px solid ${c.divider}` : "none", marginLeft: depth ? 4 : 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <select value={group.combinator ?? "and"} onChange={(e) => patchSelf({ combinator: e.target.value })} style={{ ...rbSelectStyle(c), width: 88, fontWeight: 700, textTransform: "uppercase" }}>
          {QUERY_COMBINATORS.map((cmb) => (
            <option key={cmb.name} value={cmb.name}>
              {cmb.label}
            </option>
          ))}
        </select>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: c.mid }}>
          <input type="checkbox" checked={!!group.not} onChange={(e) => patchSelf({ not: e.target.checked })} />
          NOT group
        </label>
        <button type="button" onClick={() => addItem(createRule())} style={btn(c)}>
          + Rule
        </button>
        <button type="button" onClick={() => addItem(createRuleGroup())} style={btn(c)}>
          + Group
        </button>
        {depth > 0 ? (
          <button type="button" onClick={removeSelf} style={btn(c, { danger: true })}>
            × Group
          </button>
        ) : null}
      </div>

      {group.rules.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: c.sub }}>No rules — add a rule or nested group.</p>
      ) : (
        group.rules.map((item, index) => (
          <div key={item.id ?? `${depth}-${index}`}>
            {index > 0 ? (
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: c.sub, margin: "2px 0 6px" }}>
                {(group.combinator ?? "and").toUpperCase()}
              </div>
            ) : null}
            {isRuleGroup(item) ? (
              <RuleGroupEditor group={item} path={[...path, index]} fields={fields} operators={operators} onQueryChange={onQueryChange} c={c} A={A} depth={depth + 1} />
            ) : (
              <RuleRow
                rule={item}
                fields={fields}
                operators={operators}
                onUpdate={(patch) => updateChild(index, patch)}
                onRemove={() => removeChild(index)}
                onClone={() => addItem({ ...createRule(item), id: createRule().id })}
                c={c}
                A={A}
              />
            )}
          </div>
        ))
      )}
    </div>
  );
}

export function ReportRuleBuilder({ query, onQueryChange, fieldNames, operators, c, A }) {
  const fields = buildFields(fieldNames);
  const root = query?.rules != null ? query : { combinator: "and", rules: [] };

  const handleChange = (updater) => {
    const next = typeof updater === "function" ? updater(root) : updater;
    onQueryChange(next);
  };

  return <RuleGroupEditor group={root} path={[]} fields={fields} operators={operators} onQueryChange={handleChange} c={c} A={A} depth={0} />;
}
